import Edit from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Chip,
  GridLegacy as Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { xsHeight, mdHeight } from "components/AppBar";
import { IssueListTooltip } from "components/IssueListTooltip";
import { tabsOffset } from "components/TabLinks";
import { FacilityStatus, FeatureFlagOverride, Issue } from "generated-graphql/graphql";
import { useCanEditFacility } from "hooks/useAuthorization";
import pluralize from "pluralize";
import { useCallback, useMemo, useState } from "react";

import { useBasicInformationIssues, useFacility, useFacilityProfileIssues } from "../useFacility";
import { EditBasicsDialog } from "./EditBasicsDialog";
import { V2DisplayOnlyField as DisplayOnlyField } from "./V2DisplayOnlyField";

export const BASICS_SECTION_ID = "basics";
export const BASICS_SECTION_ICON = "🏭";

/** Group issues by their key for per-field display */
function useIssuesByKey(issues: Issue[]) {
  return useMemo(() => {
    const map = new Map<string, Issue[]>();
    for (const issue of issues) {
      if (issue.key) {
        const existing = map.get(issue.key) ?? [];
        existing.push(issue);
        map.set(issue.key, existing);
      }
    }
    return map;
  }, [issues]);
}

export const BasicsSection: React.FC = () => {
  const theme = useTheme();
  const { data } = useFacility();
  const facility = data?.facility;
  const [editOpen, setEditOpen] = useState(false);
  const canEdit = useCanEditFacility();

  const isCA = facility?.state === "CA";
  const allProfileIssues = useFacilityProfileIssues({ omitStateFields: isCA });
  const basicsIssues = useBasicInformationIssues();
  const issuesByKey = useIssuesByKey(basicsIssues);

  const enabledPrograms = useMemo(() => {
    const programs: string[] = [];
    if (facility?.chemicalFeatureFlagOverride !== FeatureFlagOverride.Disabled) {
      programs.push("Chemicals");
    }
    if (facility?.wasteFeatureFlagOverride !== FeatureFlagOverride.Disabled) {
      programs.push("Waste");
    }
    return programs;
  }, [facility?.chemicalFeatureFlagOverride, facility?.wasteFeatureFlagOverride]);

  const sortedGovernmentIds = useMemo(() => {
    const ids = facility?.facilityAlternateIds ?? [];
    return [...ids].sort((a, b) => (a.type ?? "").localeCompare(b.type ?? ""));
  }, [facility?.facilityAlternateIds]);

  const tags = useMemo(() => {
    return facility?.tags?.map((t) => t.name) ?? [];
  }, [facility?.tags]);

  const colocatedDisplay = useMemo(() => {
    if (!facility?.colocationReportingFacility) return "No";
    return `Yes — ${facility.colocationReportingFacility.name}`;
  }, [facility?.colocationReportingFacility]);

  // Get issues for FacilityAlternateId model entries
  const altIdIssues = useMemo(
    () => basicsIssues.filter((i: Issue) => i.modelName === "FacilityAlternateId"),
    [basicsIssues]
  );

  return (
    <Box
      id={BASICS_SECTION_ID}
      sx={{
        scrollMarginTop: {
          xs: theme.spacing(xsHeight + tabsOffset + 11),
          md: theme.spacing(mdHeight + tabsOffset + 11),
        },
        "& .edit-icon": { opacity: 0, transition: "opacity 200ms" },
        "&:hover .edit-icon": { opacity: 1 },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={5}>
        <Typography variant="h6">{BASICS_SECTION_ICON} Basics</Typography>
        <Stack direction="row" alignItems="center" spacing={2}>
          <SectionIssueCount issues={allProfileIssues} />
          {canEdit && (
            <Tooltip title="Edit Section">
              <IconButton
                size="small"
                className="edit-icon"
                sx={{ color: "action.active" }}
                onClick={() => setEditOpen(true)}
              >
                <Edit />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {editOpen && <EditBasicsDialog onClose={() => setEditOpen(false)} />}

      {/* Facility Name — full width */}
      <Box mb={2}>
        <DisplayOnlyField
          label="Facility Name"
          text={facility?.name ?? ""}
          issues={issuesByKey.get("name")}
        />
      </Box>

      {/* 3-column grid for the rest */}
      <Grid container columnSpacing={3} rowSpacing={3}>
        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField
            label="Facility ID"
            text={facility?.customerFacilityId ?? ""}
            issues={issuesByKey.get("customerFacilityId")}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField
            label="Company Name"
            text={facility?.companyName ?? ""}
            issues={issuesByKey.get("companyName")}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField
            label="Department"
            text={facility?.department ?? ""}
            issues={issuesByKey.get("department")}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField
            label="Business Unit"
            text={facility?.businessUnit ?? ""}
            issues={issuesByKey.get("businessUnit")}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField
            label="Phone Number"
            text={facility?.phone ?? ""}
            issues={issuesByKey.get("phone")}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField
            label="24-Hour Phone Number"
            text={facility?.phone24Hour ?? ""}
            issues={issuesByKey.get("phone24Hour")}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField
            label="Dun & Bradstreet Number"
            text={facility?.dbNumber ?? ""}
            issues={issuesByKey.get("dbNumber")}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField
            label="NAICS Code"
            text={facility?.naicsCode?.toString() ?? ""}
            issues={issuesByKey.get("naicsCode")}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField
            label="Facility Type"
            text={facility?.facilityType ?? ""}
            issues={issuesByKey.get("facilityType")}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField
            label="Facility Status"
            text={facility?.status ?? ""}
            issues={issuesByKey.get("status")}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField
            label="Facility Status Reason"
            text={facility?.statusReason ?? ""}
            issues={issuesByKey.get("statusReason")}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField
            label="Date Opened"
            text={facility?.dateOpened ?? ""}
            issues={issuesByKey.get("dateOpened")}
          />
        </Grid>
        {(facility?.status === FacilityStatus.Closed ||
          facility?.status === FacilityStatus.Inactive) && (
          <>
            <Grid item xs={12} sm={6} lg={4}>
              <DisplayOnlyField
                label="Date Closed"
                text={facility?.dateClosed ?? ""}
                issues={issuesByKey.get("dateClosed")}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <DisplayOnlyField
                label="Date Inactive"
                text={facility?.dateInactive ?? ""}
                issues={issuesByKey.get("dateInactive")}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField
            label="Max Occupancy"
            text={facility?.isUnmanned ? "Unmanned" : (facility?.maxNumOccupants?.toString() ?? "")}
            issues={issuesByKey.get("maxNumOccupants")}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField
            label="Colocated"
            text={colocatedDisplay}
            issues={issuesByKey.get("colocationReportingFacilityId")}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField
            label="Encamp as Billing Contact"
            text={facility?.useEncampBillingContact ? "Yes" : "No"}
            issues={issuesByKey.get("useEncampBillingContact")}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField
            label="Tags"
            renderValue={() => (
              <Stack direction="row" flexWrap="wrap" gap={0.5}>
                {tags.map((tag) => (
                  <Chip key={tag} label={tag} size="small" />
                ))}
              </Stack>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={4}>
          <DisplayOnlyField
            label="Enabled Programs"
            renderValue={() => (
              <Stack direction="row" flexWrap="wrap" gap={0.5}>
                {enabledPrograms.map((program) => (
                  <Chip key={program} label={program} size="small" />
                ))}
              </Stack>
            )}
          />
        </Grid>

        {/* Government IDs — sorted alphabetically by type */}
        {sortedGovernmentIds.map((altId) => (
          <Grid item xs={12} sm={6} lg={4} key={altId.id}>
            <DisplayOnlyField
              label={altId.type ?? "Government ID"}
              text={altId.hasNoId ? "No ID" : (altId.value ?? "")}
              issues={altIdIssues.filter((i) => i.modelId === altId.id)}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

/** Gray compliance issue count with tooltip, for section headers */
function SectionIssueCount({ issues }: { issues: Issue[] }) {
  const [open, setOpen] = useState(false);
  const toggleTooltip = useCallback(() => setOpen((o) => !o), []);

  const label = useMemo(() => {
    const count = issues.length;
    return `${count > 0 ? count : "No"} Compliance ${pluralize("Issue", count)}`;
  }, [issues.length]);

  return (
    <IssueListTooltip
      issues={issues}
      open={open}
      onClose={() => setOpen(false)}
      header={label}
      placement="bottom-end"
    >
      <Button
        type="button"
        onClick={toggleTooltip}
        disabled={issues.length === 0}
        variant="text"
        sx={{
          color: "text.secondary",
          "&.Mui-disabled": { color: "text.secondary" },
          textTransform: "uppercase",
          fontSize: "0.75rem",
        }}
      >
        {label}
      </Button>
    </IssueListTooltip>
  );
}
